import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "cop-row" }
const _hoisted_2 = ["title"]
const _hoisted_3 = {
  class: "cop-av",
  "aria-hidden": "true"
}
const _hoisted_4 = {
  key: 0,
  class: "cop-av-check"
}
const _hoisted_5 = { class: "cop-who-text" }
const _hoisted_6 = {
  class: "cop-stage",
  "aria-live": "polite"
}
const _hoisted_7 = {
  key: 0,
  class: "cop-dots",
  "aria-hidden": "true"
}
const _hoisted_8 = ["checked", "disabled"]
const _hoisted_9 = ["title"]
const _hoisted_10 = {
  key: 0,
  class: "cop-milestone",
  role: "status"
}
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "cop-card-head" }
const _hoisted_13 = ["title"]
const _hoisted_14 = ["title"]
const _hoisted_15 = {
  key: 1,
  class: "cop-source cop-source--none"
}
const _hoisted_16 = {
  key: 2,
  class: "cop-tone-tag"
}
const _hoisted_17 = {
  key: 0,
  class: "cop-text"
}
const _hoisted_18 = {
  key: 0,
  class: "cop-caret",
  "aria-hidden": "true"
}
const _hoisted_19 = {
  key: 1,
  class: "cop-hint"
}
const _hoisted_20 = { class: "cop-actions" }
const _hoisted_21 = {
  class: "cop-tones",
  role: "group",
  "aria-label": "Reescrever noutro tom"
}
const _hoisted_22 = ["disabled", "title", "onClick"]
const _hoisted_23 = {
  key: 0,
  class: "cop-spin",
  "aria-hidden": "true"
}
const _hoisted_24 = {
  class: "cop-rate",
  role: "group",
  "aria-label": "Avaliar a sugestão"
}
const _hoisted_25 = ["aria-pressed"]
const _hoisted_26 = ["aria-pressed"]
const _hoisted_27 = {
  key: 2,
  class: "cop-problem",
  role: "alert"
}
const _hoisted_28 = {
  key: 0,
  class: "cop-hint cop-auto-note"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { onCopilotStatus, onCopilotSuggestion, type CopilotStatusNotification, type CopilotSuggestionNotification } from '@/composables/useConversationRealtime'
import {
  decideCopilotSuggestion, decisionFor, getCopilotState, rateCopilotSuggestion, requestCopilotSuggestion, rewriteCopilotSuggestion, setCopilotAutoSend,
  type CopilotDecision, type CopilotState, type CopilotTone,
} from '@/services/wootdashCopilot'

type Stage = 'idle' | 'reading' | 'writing' | 'ready' | 'none'
type Confidence = 'alta' | 'media' | 'baixa'

const AUTO_HINT = 'Ligado, a sugestão sai sozinha em seu nome, marcada como copiloto (menos quando o agente quer transferir ou uma ferramenta falhou).'

export default /*@__PURE__*/_defineComponent({
  __name: 'CopilotBar',
  props: {
    conversationId: {},
    draft: {}
  },
  emits: ["fill"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

// Copiloto de IA no chat do atendente. A sugestão chega por SignalR (SugestaoCopiloto) e o que o agente de IA está fazendo
// (CopilotoStatus: lendo, escrevendo, sem sugestão). Com o campo vazio a sugestão já entra no campo; com texto digitado,
// fica no cartão pra "Usar" (Tab) ou "Descartar" (Esc). O pai avisa o envio (exposto: sent) pra medir usada/editada/descartada.
const props = __props
const emit = __emit

const STAGE_TEXT: Record<Stage, string> = {
  idle: 'pronto pra ajudar',
  reading: 'lendo a conversa…',
  writing: 'escrevendo uma sugestão',
  ready: 'sugestão pronta',
  none: 'sem sugestão pra esta mensagem',
}
const CONF_LABEL: Record<Confidence, string> = { alta: 'alta', media: 'média', baixa: 'baixa' }
const CONF_HINT: Record<Confidence, string> = {
  alta: 'Muito parecida com uma dúvida frequente revisada ou um documento da base.',
  media: 'Parecida com uma dúvida frequente ou documento — vale uma conferida.',
  baixa: 'Sem base suficiente (ou o agente quis transferir) — revise antes de enviar.',
}
const TONES: { value: CopilotTone; label: string; hint: string }[] = [
  { value: 'curta', label: 'Mais curta', hint: 'A mesma resposta, mais direta' },
  { value: 'formal', label: 'Mais formal', hint: 'A mesma resposta, mais profissional' },
  { value: 'acolhedora', label: 'Mais acolhedora', hint: 'A mesma resposta, mais simpática' },
]
const TONE_LABEL: Record<CopilotTone, string> = { curta: 'mais curta', formal: 'mais formal', acolhedora: 'mais acolhedora' }
const MILESTONES = [10, 25, 50, 100]

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')

const root = ref<HTMLElement | null>(null)
const state = ref<CopilotState | null>(null)
const suggestion = ref<CopilotSuggestionNotification | null>(null)
const stage = ref<Stage>('idle')
const savingAuto = ref(false)
const asking = ref(false)
const autoNote = ref<string | null>(null)
const rewriting = ref<CopilotTone | null>(null)
const rewriteProblem = ref<string | null>(null)
const rated = ref<boolean | null>(null)
const milestone = ref<string | null>(null)
const typed = ref('')
const typing = ref(false)
let unsubscribe: (() => void)[] = []
let stageTimer: ReturnType<typeof setTimeout> | null = null
let typeTimer: ReturnType<typeof setInterval> | null = null
let milestoneTimer: ReturnType<typeof setTimeout> | null = null

const today = computed(() => state.value?.today ?? null)
const confidence = computed<Confidence>(() => suggestion.value?.confidence ?? (suggestion.value?.safe ? 'media' : 'baixa'))
/** A sugestão está no campo (do jeito que veio ou editada a partir dela). */
const inDraft = computed(() => !!suggestion.value && props.draft.trim().length > 0 && decisionFor(suggestion.value.text, props.draft) !== 'discarded')
const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function setStage(next: Stage) {
  stage.value = next
  if (stageTimer) clearTimeout(stageTimer)
  // "Pronta"/"sem sugestão" voltam a "pronto pra ajudar" depois de um tempo; "escrevendo" sem resposta também.
  if (next === 'none' || next === 'ready') stageTimer = setTimeout(() => { stage.value = 'idle' }, next === 'none' ? 5000 : 8000)
  if (next === 'reading' || next === 'writing') stageTimer = setTimeout(() => { stage.value = 'idle' }, 60000)
}

/** Efeito de digitação curto (~0,7 s, qualquer tamanho) — some com "reduzir movimento". */
function typeOut(text: string) {
  if (typeTimer) clearInterval(typeTimer)
  if (reducedMotion) { typed.value = text; typing.value = false; return }
  const step = Math.max(2, Math.ceil(text.length / 35))
  let i = 0
  typing.value = true
  typed.value = ''
  typeTimer = setInterval(() => {
    i = Math.min(text.length, i + step)
    typed.value = text.slice(0, i)
    if (i >= text.length) { clearInterval(typeTimer!); typeTimer = null; typing.value = false }
  }, 20)
}

function showSuggestion(evt: CopilotSuggestionNotification) {
  suggestion.value = evt
  rated.value = null
  rewriteProblem.value = null
  typeOut(evt.text)
}

function receive(evt: CopilotSuggestionNotification) {
  asking.value = false
  if (evt.status === 'auto_sent') {
    suggestion.value = null
    setStage('ready')
    autoNote.value = `O copiloto respondeu sozinho às ${new Date(evt.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}.`
    return
  }
  autoNote.value = null
  setStage('ready')
  showSuggestion(evt)
  if (!props.draft.trim()) emit('fill', evt.text)
}

function onStatus(evt: CopilotStatusNotification) {
  if (evt.stage === 'none') { asking.value = false; setStage('none') }
  else setStage(evt.stage)
}

async function load(conversationId: string) {
  unsubscribe.forEach(u => u())
  unsubscribe = []
  suggestion.value = null
  autoNote.value = null
  stage.value = 'idle'
  state.value = await getCopilotState(token.value, conversationId)
  if (!state.value?.active) return
  unsubscribe = [onCopilotSuggestion(conversationId, receive), onCopilotStatus(conversationId, onStatus)]
  if (state.value.pending) showSuggestion(state.value.pending)
}

async function toggleAuto(on: boolean) {
  if (!state.value) return
  savingAuto.value = true
  const ok = await setCopilotAutoSend(token.value, props.conversationId, on)
  savingAuto.value = false
  if (ok) state.value = { ...state.value, autoSend: on }
}

async function askAgain() {
  asking.value = true
  if (!(await requestCopilotSuggestion(token.value, props.conversationId))) { asking.value = false; return }
  setStage('reading')
  // A resposta chega pelo SignalR (receive/onStatus); sem mensagem do contato, nada chega.
  setTimeout(() => { asking.value = false }, 30000)
}

function composer(): HTMLTextAreaElement | HTMLInputElement | null {
  return root.value?.parentElement?.querySelector('textarea, input[type="text"]:not([readonly])') ?? null
}

function use() {
  if (suggestion.value) emit('fill', suggestion.value.text)
}

function edit() {
  use()
  setTimeout(() => { const el = composer(); el?.focus(); if (el) el.selectionStart = el.selectionEnd = el.value.length }, 30)
}

function countDecision(decision: CopilotDecision) {
  const t = state.value?.today
  if (!state.value || !t) return
  const next = { ...t, [decision]: t[decision] + 1 }
  state.value = { ...state.value, today: next }
  const before = t.used + t.edited
  const after = next.used + next.edited
  const hit = MILESTONES.find(m => before < m && after >= m)
  if (hit) celebrate(`${hit} sugestões aproveitadas hoje`)
}

function celebrate(text: string) {
  milestone.value = text
  if (milestoneTimer) clearTimeout(milestoneTimer)
  milestoneTimer = setTimeout(() => { milestone.value = null }, 4500)
}

function discard() {
  if (!suggestion.value) return
  const current = suggestion.value
  decideCopilotSuggestion(token.value, current.suggestionId, 'discarded')
  countDecision('discarded')
  if (props.draft.trim() === current.text.trim()) emit('fill', '')
  suggestion.value = null
}

async function rewrite(tone: CopilotTone) {
  if (!suggestion.value) return
  const current = suggestion.value
  rewriting.value = tone
  rewriteProblem.value = null
  const result = await rewriteCopilotSuggestion(token.value, current.suggestionId, tone)
  rewriting.value = null
  if (suggestion.value?.suggestionId !== current.suggestionId) return
  if (!result.success || !result.text) { rewriteProblem.value = result.problem ?? 'Não deu pra reescrever agora.'; return }
  const wasInField = props.draft.trim() === current.text.trim() || !props.draft.trim()
  suggestion.value = { ...current, text: result.text, tone: result.tone }
  typeOut(result.text)
  if (wasInField) emit('fill', result.text)
}

function rate(helpful: boolean) {
  if (!suggestion.value) return
  rated.value = helpful
  rateCopilotSuggestion(token.value, suggestion.value.suggestionId, helpful)
  if (state.value?.today && rated.value !== null) {
    const t = state.value.today
    state.value = { ...state.value, today: { ...t, helpful: t.helpful + (helpful ? 1 : 0), notHelpful: t.notHelpful + (helpful ? 0 : 1) } }
  }
}

/** O pai chama depois de enviar uma mensagem (não nota interna). */
function sent(text: string) {
  if (!suggestion.value) return
  const decision = decisionFor(suggestion.value.text, text)
  decideCopilotSuggestion(token.value, suggestion.value.suggestionId, decision)
  countDecision(decision)
  suggestion.value = null
}

__expose({ sent })

/** Tab = usar, Esc = descartar — só com o foco no campo de mensagem deste chat e o cartão aberto. */
function onKey(e: KeyboardEvent) {
  if (!suggestion.value || e.altKey || e.ctrlKey || e.metaKey) return
  const target = e.target as HTMLElement | null
  if (!target || !root.value?.parentElement?.contains(target) || root.value.contains(target)) return
  if (!(target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)) return
  if (e.key === 'Tab' && !e.shiftKey && !inDraft.value && !props.draft.trim()) { e.preventDefault(); use() }
  else if (e.key === 'Escape') { e.preventDefault(); discard() }
}

watch(() => props.conversationId, id => { if (id) load(id) }, { immediate: true })
onMounted(() => document.addEventListener('keydown', onKey, true))
onBeforeUnmount(() => {
  unsubscribe.forEach(u => u())
  document.removeEventListener('keydown', onKey, true)
  for (const t of [stageTimer, milestoneTimer]) if (t) clearTimeout(t)
  if (typeTimer) clearInterval(typeTimer)
})

return (_ctx: any,_cache: any) => {
  return (state.value?.active)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "root",
        ref: root,
        class: _normalizeClass(["cop", `is-${stage.value}`])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", {
            class: "cop-who",
            title: `O agente de IA ${state.value.agentName} sugere as respostas neste Fluxo`
          }, [
            _createElementVNode("span", _hoisted_3, [
              _cache[3] || (_cache[3] = _createElementVNode("svg", {
                width: "14",
                height: "14",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "2"
              }, [
                _createElementVNode("rect", {
                  x: "4",
                  y: "7",
                  width: "16",
                  height: "12",
                  rx: "3"
                }),
                _createElementVNode("path", { d: "M12 3v4M9 12h.01M15 12h.01M9 16h6" })
              ], -1)),
              (stage.value === 'ready')
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, "✓"))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("span", _hoisted_5, [
              _createElementVNode("b", null, _toDisplayString(state.value.agentName), 1),
              _createElementVNode("span", _hoisted_6, [
                _createTextVNode(_toDisplayString(STAGE_TEXT[stage.value]) + " ", 1),
                (stage.value === 'writing')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("i", null, null, -1),
                      _createElementVNode("i", null, null, -1),
                      _createElementVNode("i", null, null, -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ])
            ])
          ], 8, _hoisted_2),
          _createElementVNode("label", {
            class: "cop-switch",
            title: AUTO_HINT
          }, [
            _createElementVNode("input", {
              id: "cop-auto",
              type: "checkbox",
              role: "switch",
              checked: state.value.autoSend,
              disabled: savingAuto.value,
              onChange: _cache[0] || (_cache[0] = ($event: any) => (toggleAuto(($event.target as HTMLInputElement).checked)))
            }, null, 40, _hoisted_8),
            _cache[5] || (_cache[5] = _createElementVNode("span", {
              class: "cop-switch-track",
              "aria-hidden": "true"
            }, [
              _createElementVNode("span", { class: "cop-switch-thumb" })
            ], -1)),
            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "cop-switch-text" }, "Enviar sozinho", -1))
          ]),
          (today.value)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: "cop-today",
                title: `Hoje: ${today.value.used} usadas como vieram, ${today.value.edited} editadas, ${today.value.discarded} descartadas`
              }, [
                _cache[7] || (_cache[7] = _createTextVNode(" Hoje: ", -1)),
                _createElementVNode("b", null, _toDisplayString(today.value.used + today.value.edited), 1),
                _cache[8] || (_cache[8] = _createTextVNode(" aproveitada(s) ", -1))
              ], 8, _hoisted_9))
            : _createCommentVNode("", true),
          _createVNode(_Transition, { name: "cop-pop" }, {
            default: _withCtx(() => [
              (milestone.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_10, "✦ " + _toDisplayString(milestone.value), 1))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createElementVNode("button", {
            type: "button",
            class: "cop-link",
            disabled: asking.value,
            onClick: askAgain
          }, _toDisplayString(asking.value ? 'Pedindo…' : 'Sugerir de novo'), 9, _hoisted_11)
        ]),
        _createVNode(_Transition, { name: "cop-card" }, {
          default: _withCtx(() => [
            (suggestion.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(['cop-card', `cop-card--${confidence.value}`])
                }, [
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("span", {
                      class: _normalizeClass(['cop-conf', `cop-conf--${confidence.value}`]),
                      title: CONF_HINT[confidence.value]
                    }, [
                      _cache[9] || (_cache[9] = _createElementVNode("span", {
                        class: "cop-conf-bars",
                        "aria-hidden": "true"
                      }, [
                        _createElementVNode("i"),
                        _createElementVNode("i"),
                        _createElementVNode("i")
                      ], -1)),
                      _createTextVNode(" Confiança " + _toDisplayString(CONF_LABEL[confidence.value]), 1)
                    ], 10, _hoisted_13),
                    (suggestion.value.sourceTitle)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: "cop-source",
                          title: suggestion.value.reason ?? ''
                        }, [
                          _cache[10] || (_cache[10] = _createTextVNode("Baseado em: ", -1)),
                          _createElementVNode("b", null, _toDisplayString(suggestion.value.sourceTitle), 1)
                        ], 8, _hoisted_14))
                      : (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(suggestion.value.reason || 'Sem base na dúvida frequente nem em documento: revise antes de enviar.'), 1)),
                    (suggestion.value.tone)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, "tom: " + _toDisplayString(TONE_LABEL[suggestion.value.tone]), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (!inDraft.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_17, [
                        _createTextVNode(_toDisplayString(typed.value), 1),
                        (typing.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_18))
                          : _createCommentVNode("", true)
                      ]))
                    : (_openBlock(), _createElementBlock("p", _hoisted_19, "Já está no campo de mensagem — edite se quiser e envie.")),
                  _createElementVNode("div", _hoisted_20, [
                    (!inDraft.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "cop-btn cop-btn--primary",
                            onClick: use
                          }, [...(_cache[11] || (_cache[11] = [
                            _createTextVNode("Usar ", -1),
                            _createElementVNode("kbd", null, "Tab", -1)
                          ]))]),
                          _createElementVNode("button", {
                            type: "button",
                            class: "cop-btn",
                            onClick: edit
                          }, "Editar")
                        ], 64))
                      : _createCommentVNode("", true),
                    _createElementVNode("button", {
                      type: "button",
                      class: "cop-btn cop-btn--ghost",
                      onClick: discard
                    }, [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("Descartar ", -1),
                      _createElementVNode("kbd", null, "Esc", -1)
                    ]))]),
                    _cache[13] || (_cache[13] = _createElementVNode("span", {
                      class: "cop-sep",
                      "aria-hidden": "true"
                    }, null, -1)),
                    _createElementVNode("div", _hoisted_21, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(TONES, (t) => {
                        return _createElementVNode("button", {
                          key: t.value,
                          type: "button",
                          class: _normalizeClass(['cop-chip', { 'is-on': suggestion.value.tone === t.value }]),
                          disabled: !!rewriting.value,
                          title: `${t.hint} — gasta alguns tokens`,
                          onClick: ($event: any) => (rewrite(t.value))
                        }, [
                          (rewriting.value === t.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_23))
                            : _createCommentVNode("", true),
                          _createTextVNode(_toDisplayString(t.label), 1)
                        ], 10, _hoisted_22)
                      }), 64))
                    ]),
                    _createElementVNode("div", _hoisted_24, [
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(['cop-thumb', { 'is-on': rated.value === true }]),
                        "aria-pressed": rated.value === true,
                        title: "Boa sugestão",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (rate(true)))
                      }, "👍", 10, _hoisted_25),
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(['cop-thumb', { 'is-on': rated.value === false }]),
                        "aria-pressed": rated.value === false,
                        title: "Sugestão ruim",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (rate(false)))
                      }, "👎", 10, _hoisted_26)
                    ])
                  ]),
                  (rewriteProblem.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_27, _toDisplayString(rewriteProblem.value), 1))
                    : _createCommentVNode("", true)
                ], 2))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        (!suggestion.value && autoNote.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_28, _toDisplayString(autoNote.value), 1))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})